/**
 * The apothecary's counter and what stands on it (lifted from lantern-water's
 * `world.js` `createShop`): the painted counter with inlaid panels, wooden
 * mouldings and brass sprig carvings (`apothecary.ts` `counterCarving`), its
 * top, the eight curated vessels, the brass edging and kick rail, the two
 * turned posts, two candles, the open ledger (`art.ts` `botanicalTexture`,
 * its writing as ruled lines), the copper scales, the seven canvas shelf tags
 * along the back shelves (z −4.19; kept here because the prototype drew them
 * with the counter's stock), and the herb tray with its sprigs. A once-built
 * piece: origin at the room's centre on the floor. No params.
 */
import * as THREE from 'three';
import { botanicalTexture } from '../art.ts';
import { apothecaryVessel, cabinetPaint, candle, counterCarving } from '../apothecary.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'shop_counter',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { m } = ctx;
    const r = ctx.random;
    const paint = cabinetPaint(ctx.kit);
    ctx.box(root, 0.3, 0.64, -2.3, 6.6, 1.28, 1.03, paint);
    ctx.box(root, 0.3, 1.32, -2.3, 6.88, 0.18, 1.25, m.wood);
    for (let x = -2.5; x < 3.5; x += 1.1) {
      ctx.box(root, x, 0.65, -1.768, 0.89, 0.94, 0.035, m.darkWood);
      ctx.box(root, x, 0.65, -1.74, 0.75, 0.8, 0.03, paint);
      for (const side of [-1, 1]) {
        ctx.box(root, x + side * 0.385, 0.65, -1.715, 0.035, 0.85, 0.028, m.wood);
        ctx.box(root, x, 0.65 + side * 0.405, -1.715, 0.8, 0.035, 0.028, m.wood);
      }
      counterCarving(ctx.kit, root, x, 0.65, -1.71, Math.round((x + 2.5) / 1.1));
    }
    // Curated groups leave the open ledger, scales and working space legible.
    for (const [x, z, size, kind] of [
      [-2.05, -2.52, 0.38, 2],
      [-1.57, -2.57, 0.29, 0],
      [-2.05, -2.0, 0.27, 1],
      [0.07, -2.64, 0.3, 3],
      [0.53, -2.66, 0.28, 0],
      [2.45, -2.52, 0.36, 2],
      [2.88, -2.55, 0.26, 1],
      [2.48, -1.98, 0.28, 0],
    ] as const) {
      apothecaryVessel(ctx.kit, root, x, 1.43, z, size, kind);
    }
    ctx.box(root, 0.3, 0.12, -1.72, 6.8, 0.12, 0.12, m.wood);
    ctx.box(root, 0.3, 1.22, -1.7, 6.8, 0.06, 0.09, m.brass);
    for (const x of [-2.96, 3.56]) {
      ctx.cylinder(root, x, 0.68, -1.68, 0.1, 1.18, paint);
      for (const y of [0.2, 1.14]) ctx.cylinder(root, x, y, -1.68, 0.13, 0.1, m.wood);
    }
    candle(ctx, root, -2.6, 1.44, -2.15);
    candle(ctx, root, 3.1, 1.44, -2.2);
    ctx.box(root, 0.4, 1.45, -1.95, 0.9, 0.08, 0.65, m.book2);
    ctx.box(root, 0.4, 1.5, -1.95, 0.85, 0.035, 0.59, m.canvas);
    ctx.box(root, 0.4, 1.52, -1.95, 0.025, 0.02, 0.59, m.darkWood);
    const ledgerMaterial = ctx.kit.shared('shop-counter:ledger', () => new THREE.MeshStandardMaterial({ map: ctx.kit.shared('shop-counter:ledger-map', () => botanicalTexture(true)), roughness: 1 }));
    const ledger = ctx.mesh(root, ctx.plane, ledgerMaterial, 0.4, 1.544, -1.95, 0.84, 0.59, 1);
    ledger.rotation.x = -Math.PI / 2;
    ledger.castShadow = false;
    // Copper scales.
    ctx.line(root, [1.7, 1.5, -2.45], [1.7, 2.05, -2.45], 0.035, m.brass);
    ctx.line(root, [1.25, 1.96, -2.45], [2.15, 1.96, -2.45], 0.035, m.brass);
    for (const sx of [-1, 1]) {
      ctx.line(root, [1.7 + sx * 0.4, 1.96, -2.45], [1.7 + sx * 0.4, 1.67, -2.45], 0.012, m.brass);
      ctx.cylinder(root, 1.7 + sx * 0.4, 1.65, -2.45, 0.19, 0.05, m.brass);
    }
    for (let i = 0; i < 7; i += 1) {
      const tag = ctx.box(root, -5.65 + i * 1.68, 2.7, -4.19, 0.26, 0.12, 0.018, m.canvas);
      tag.rotation.z = (r() - 0.5) * 0.12;
    }
    // The herb tray.
    ctx.box(root, -1.15, 1.46, -2.15, 0.8, 0.05, 0.43, m.darkWood);
    for (let i = 0; i < 14; i += 1) {
      const ax = -1.45 + r() * 0.6;
      const az = -2.3 + r() * 0.3;
      const bx = -1.45 + r() * 0.6;
      const bz = -2.3 + r() * 0.3;
      ctx.line(root, [ax, 1.51, az], [bx, 1.52, bz], 0.026, m.moss);
    }
    return { root };
  },
};
